import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Turnstile } from "@marsidev/react-turnstile";
import { Link } from "react-router-dom";
import { createTextJob, deleteTextJob, getTextJob, textCheckerAvailable } from "./textApi";
import { AdPlacement } from "./AdPlacement";
import { automaticPollingStopped, nextJobPollInterval } from "./polling";

const MAX_CHARS = 12_000;
type ActiveJob = { id: string; token: string };

export function textAdEligible(job: ActiveJob | null, passage: string): boolean {
  return job !== null && passage.length === 0;
}

export function TextPage() {
  const [text, setText] = useState("");
  const [job, setJob] = useState<ActiveJob | null>(null);
  const [challenge, setChallenge] = useState<string | null>(null);
  const [challengeKey, setChallengeKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
  const availability = useQuery({ queryKey: ["text-availability"], queryFn: textCheckerAvailable, retry: false, staleTime: 15_000, refetchInterval: 30_000, refetchIntervalInBackground: false });
  const enabled = availability.data === true;
  const words = (text.match(/[A-Za-z]+(?:['’][A-Za-z]+)*/g) || []).length;
  const canSubmit = enabled && !submitting && !job && text.trim().length >= 1000
    && text.trim().length <= MAX_CHARS && words >= 150 && (!siteKey || Boolean(challenge));
  const query = useQuery({
    queryKey: ["text-job", job?.id],
    queryFn: () => getTextJob(job!.id, job!.token),
    enabled: Boolean(job),
    retry: false,
    refetchIntervalInBackground: false,
    refetchInterval: (current) => current.state.error ? false : nextJobPollInterval(current.state.data),
  });

  async function submit() {
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      const created = await createTextJob(text.trim(), challenge || undefined);
      setText("");
      setJob({ id: created.jobId, token: created.accessToken });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The check could not be started.");
      setChallenge(null);
      setChallengeKey((value) => value + 1);
    } finally {
      setSubmitting(false);
    }
  }

  async function clear() {
    if (deleting) return;
    setDeleting(true);
    try {
      if (job) await deleteTextJob(job.id, job.token);
      // A full reload discards the ad script before the writing form returns.
      window.location.assign("/text");
    } catch {
      setError("We could not delete this check right now. Please try again.");
    } finally {
      setDeleting(false);
    }
  }

  const result = query.data?.result;
  const pollingStopped = automaticPollingStopped(query.data);
  const tone = result?.verdict === "AI_SIGNALS" ? "ai" : result?.verdict === "NO_STRONG_SIGNALS" ? "human" : "neutral";
  const label = result?.verdict === "AI_SIGNALS" ? "AI writing signals found"
    : result?.verdict === "NO_STRONG_SIGNALS" ? "No strong AI writing signals" : "Not sure";

  return <article className="text-page">
    <nav className="detector-switcher" aria-label="Choose a detector"><Link to="/">Image detector</Link><span aria-current="page">Text detector</span></nav>
    <header className="text-page-heading"><span className="eyebrow">FREE AI TEXT DETECTOR</span><h1>Does this sound AI-written?</h1><p>Paste a passage for a free check of patterns associated with AI writing. No account needed—and your result is always an estimate, not proof of authorship.</p><div className="text-benefits" aria-label="Text detector benefits"><span>Free to use</span><span>No sign-up</span><span>Private by design</span></div><Link className="inline-tool-link" to="/write">Want to improve a draft? Open the free writing assistant →</Link></header>
    {!enabled ? <section className="text-checker-card" aria-live="polite"><h2>Text checking is temporarily unavailable</h2><p>New checks are paused instead of placing you in a long queue. Please try again shortly.</p><button type="button" className="secondary-button" onClick={() => availability.refetch()}>Check availability</button><Link className="inline-tool-link" to="/">Check an image instead</Link></section>
      : <section className="text-checker-card" aria-label="Text checker">
        {!job ? <>
          <label htmlFor="text-input">Paste your writing</label>
          <textarea id="text-input" value={text} maxLength={MAX_CHARS} onChange={(event) => setText(event.target.value)} placeholder="Paste an English passage of at least 1,000 characters and 150 words…" aria-describedby="text-guidance text-count" />
          <div className="text-input-footer"><p id="text-guidance">Best for original, continuous English prose. Short, translated, heavily edited, or mixed-format writing may be inconclusive.</p><span id="text-count">{text.length.toLocaleString()} / {MAX_CHARS.toLocaleString()} characters · {words} words</span></div>
          {siteKey && <div className="turnstile-wrap"><Turnstile key={challengeKey} siteKey={siteKey} onSuccess={setChallenge} onExpire={() => setChallenge(null)} options={{ theme: "light", size: "flexible" }} /></div>}
          <button type="button" className="primary-button" disabled={!canSubmit} onClick={submit}>{submitting ? "Starting check…" : "Check writing"}</button>
          <p className="text-privacy-note">Your passage is encrypted for checking, then deleted after analysis. Results expire after 24 hours. We do not use your writing to train our detector.</p>
        </> : <>
          {!result && !query.isError && !["FAILED", "EXPIRED"].includes(query.data?.status || "") && <div className="processing" role="status">{!pollingStopped && <span className="spinner" />}<div><strong>{pollingStopped ? "This check is taking longer than expected" : "Checking your writing…"}</strong><p>{pollingStopped ? "Automatic updates are paused to avoid unnecessary requests. Your check remains in the queue." : "Updates slow down automatically if the queue is busy."}</p>{pollingStopped && <button className="refresh-status-button" type="button" onClick={() => query.refetch()}>Check status now</button>}</div></div>}
          {result && <section className={`text-result result-${tone}`} aria-live="polite"><span className="section-kicker">YOUR RESULT</span><h2>{label}</h2><p>{result.summary}</p><ul>{result.limitations.map((note) => <li key={note}>{note}</li>)}</ul></section>}
          {query.data?.status === "FAILED" && <p className="error-message" role="alert">{query.data.error?.message || "The check could not be completed."}</p>}
          {query.data?.status === "EXPIRED" && <p className="error-message" role="alert">This check has expired. Please start a new one.</p>}
          {query.isError && <p className="error-message" role="alert">{query.error instanceof Error ? query.error.message : "We could not refresh your result."} <button type="button" onClick={() => query.refetch()}>Try again</button></p>}
          <button type="button" className="secondary-button" disabled={deleting} onClick={clear}>{deleting ? "Deleting…" : "Delete this check"}</button>
        </>}
        {error && <p className="error-message" role="alert">{error}</p>}
      </section>}
    <section className="text-explainer"><h2>What this result means</h2><p>AI writing detection is an estimate, not an authorship test. A person’s style can resemble AI output, and AI text can be edited to look human. Never use this result alone to accuse, grade, hire, or penalize anyone.</p><Link className="inline-tool-link" to="/ai-text-detector">Read the writing detection guide</Link><p><Link className="inline-tool-link" to="/write">Improve grammar and flow with our private writing editor</Link></p></section>
    <aside className="image-detector-promo"><span><small>CHECKING AN IMAGE?</small><strong>Detect AI images, edits, and deepfakes</strong><p>Upload a photo or graphic for a free Real, Fake, or Not Sure result.</p></span><Link to="/">Open image detector</Link></aside>
    {textAdEligible(job, text) && <AdPlacement placement="text" />}
  </article>;
}
